import { useAtomValue } from 'jotai';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { userSettingsQueryAtom } from 'src/contexts/SellerUserSettingsContext/SellerUserAllSettingsContext.atom';
import { useServerUserSetting } from 'src/hooks/useUserSetting';
import { UserSetting } from 'src/WebApiController';

import { DEFAULT_ICON_KEY } from './customTabIcons';
import { CustomTab, CustomTabScope, CustomTabType } from './types';

const STORAGE_KEY = 'pos_custom_tabs';

function applyDefaults(tab: Partial<CustomTab>): CustomTab {
  return {
    id: tab.id ?? crypto.randomUUID(),
    label: tab.label ?? '',
    description: tab.description ?? '',
    icon: tab.icon ?? DEFAULT_ICON_KEY,
    tabType: (tab.tabType as CustomTabType) ?? 'Link',
    url: tab.url,
    snippet: tab.snippet,
    openInNewTab: tab.openInNewTab !== false,
    scope: (tab.scope as CustomTabScope) ?? 'TopLevel',
  };
}

function loadLocalTabs(): CustomTab[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.map(applyDefaults);
  } catch {
    return [];
  }
}

function saveLocalTabs(tabs: CustomTab[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(tabs));
  } catch {
    // ignore storage errors
  }
}

export interface UseCustomTabsResult {
  tabs: CustomTab[];
  addTab: (tab: Omit<CustomTab, 'id'>) => CustomTab;
  updateTab: (id: string, updates: Partial<CustomTab>) => void;
  deleteTab: (id: string) => void;
  reorderTabs: (reordered: CustomTab[]) => void;
  isLoading: boolean;
}

export function useCustomTabs(): UseCustomTabsResult {
  const {
    value: serverTabs,
    setUserSetting,
    isLoading,
  } = useServerUserSetting<CustomTab[]>({
    id: UserSetting.CustomTabs,
  });

  // The prefetch atom query tells us whether the bulk user-settings fetch
  // has completed.  `isFetched` is stable once the query resolves and is
  // not affected by React Query v5's `isLoading` = `isPending && isFetching`
  // change that makes `isLoading` unreliable for disabled-then-enabled queries.
  const prefetchQuery = useAtomValue(userSettingsQueryAtom);
  const isPrefetchDone = prefetchQuery.isFetched;

  // Local state is the source of truth for the UI.
  // It is initialized from localStorage for instant render, then reconciled
  // with the server value once it arrives.
  const [tabs, setTabs] = useState<CustomTab[]>(loadLocalTabs);
  const hasMigratedRef = useRef(false);

  // Reconcile server data with local state once the prefetch has completed.
  //
  // `isPrefetchDone` flips to `true` exactly once — after the bulk
  // `getAllSellerUserSettings` call resolves.  At that point `serverTabs`
  // is the real server state: either a `CustomTab[]` or `undefined`
  // (meaning no server entry exists for this setting).
  useEffect(() => {
    if (!isPrefetchDone || hasMigratedRef.current) return;
    // If the prefetch failed, don't attempt migration — the write would
    // also fail and we can't distinguish "no server data" from "server error".
    if (prefetchQuery.isError) return;
    hasMigratedRef.current = true;

    const localTabs = loadLocalTabs();

    if (serverTabs && serverTabs.length > 0) {
      // Server has data — use it as the source of truth
      const hydrated = serverTabs.map(applyDefaults);
      setTabs(hydrated);
      saveLocalTabs(hydrated);
    } else if (localTabs.length > 0) {
      // Server is empty but localStorage has tabs — migrate them to the server
      setUserSetting(localTabs);
    }
    // If both are empty, tabs stays as [] (the initial state)
  }, [isPrefetchDone, serverTabs, setUserSetting]);

  // Keep a ref to the latest tabs so callbacks can read current state
  // without needing tabs in their dependency arrays.
  const tabsRef = useRef(tabs);
  tabsRef.current = tabs;

  const persistTabs = useCallback(
    (updated: CustomTab[]) => {
      saveLocalTabs(updated);
      setUserSetting(updated);
    },
    [setUserSetting]
  );

  // All mutation helpers compute the next state, update React state,
  // then persist.  Persist is called *outside* the state updater to
  // avoid double-writes in React Strict Mode (which intentionally
  // double-invokes updater functions in development).

  const addTab = useCallback(
    (tab: Omit<CustomTab, 'id'>): CustomTab => {
      const newTab = applyDefaults({ ...tab, id: crypto.randomUUID() });
      const next = [...tabsRef.current, newTab];
      setTabs(next);
      persistTabs(next);
      return newTab;
    },
    [persistTabs]
  );

  const updateTab = useCallback(
    (id: string, updates: Partial<CustomTab>): void => {
      const next = tabsRef.current.map((t) =>
        t.id === id ? applyDefaults({ ...t, ...updates }) : t
      );
      setTabs(next);
      persistTabs(next);
    },
    [persistTabs]
  );

  const deleteTab = useCallback(
    (id: string): void => {
      const next = tabsRef.current.filter((t) => t.id !== id);
      setTabs(next);
      persistTabs(next);
    },
    [persistTabs]
  );

  const reorderTabs = useCallback(
    (reordered: CustomTab[]): void => {
      setTabs(reordered);
      persistTabs(reordered);
    },
    [persistTabs]
  );

  return useMemo(
    () => ({ tabs, addTab, updateTab, deleteTab, reorderTabs, isLoading }),
    [tabs, addTab, updateTab, deleteTab, reorderTabs, isLoading]
  );
}
